import type { ThunkDispatch } from '@reduxjs/toolkit'
import type { Types as OData } from 'shared/odata'

import { apiRTK, Tag } from 'api/api-rtk'

import type {
	AudienceParams,
	Campaign,
	ExecutionsStatistics,
	RepliedCampaignExecutionsStatistics
} from './types'

import { RECENT_PARAMS } from './const'

const PAGE_ID = [{ id: 'Page', type: Tag.Campaign }]
const RECENT_ID = [{ id: 'Recent', type: Tag.Campaign }]

const invalidateAll = (campaign?: Campaign) =>
	campaign ? [...PAGE_ID, Tag.Contact, Tag.Executions] : []

export const api = apiRTK.injectEndpoints({
	endpoints: create => ({
		archiveCampaign: create.mutation<Campaign, string>({
			invalidatesTags: PAGE_ID,
			onQueryStarted: updateCacheCampaign,
			query: id => ({
				method: 'PUT',
				url: `campaigns/${id}/archive`
			})
		}),
		audienceDelete: create.mutation<Campaign, AudienceParams>({
			invalidatesTags: invalidateAll,
			query: ({ id, ...params }) => ({
				method: 'DELETE',
				params,
				url: `campaigns/${id}/target_audience`
			})
		}),
		audienceUpdate: create.mutation<Campaign, AudienceParams>({
			invalidatesTags: invalidateAll,
			onQueryStarted: updateCacheCampaign,
			query: ({ id, ...params }) => ({
				method: 'PUT',
				params,
				url: `campaigns/${id}/target_audience`
			})
		}),
		audienceValidate: create.mutation<OData.ValidationPreview, AudienceParams>({
			query: ({ id, ...params }) => ({
				method: 'GET',
				params,
				url: `campaigns/${id}/target_audience/preview`
			})
		}),
		campaignExecutionsStatistics: create.query<ExecutionsStatistics, string>({
			query: id => ({
				url: `campaigns/${id}/executions-statistics`
			})
		}),
		campaignPreview: create.mutation<OData.ValidationPreview, string>({
			query: id => ({
				method: 'GET',
				url: `campaigns/${id}/preview`
			})
		}),
		campaignsPage: create.query<
			OData.PageResponse<Campaign>,
			Partial<OData.Page>
		>({
			async onQueryStarted(_args, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled

					for (const campaign of data.documents) {
						addCampaignToCache(campaign, dispatch)
					}
				} catch (error) {
					return
				}
			},

			providesTags: (_result, _error, args) =>
				args === RECENT_PARAMS ? RECENT_ID : PAGE_ID,
			query: params => ({
				params,
				url: 'campaigns/page'
			})
		}),
		cloneCampaign: create.mutation<Campaign, string>({
			invalidatesTags: PAGE_ID,
			onQueryStarted: updateCacheCampaign,
			query: id => ({
				method: 'PUT',
				url: `campaigns/${id}/clone`
			})
		}),
		completeCampaign: create.mutation<Campaign, string>({
			invalidatesTags: [...PAGE_ID, Tag.ExecutionsStatistics],
			onQueryStarted: updateCacheCampaign,
			query: id => ({
				method: 'PUT',
				url: `campaigns/${id}/complete`
			})
		}),
		createCampaign: create.mutation<Campaign, string>({
			// invalidatesTags: PAGE_ID, should refetch on mount
			async onQueryStarted(_, { dispatch, queryFulfilled }) {
				const { data } = await queryFulfilled

				addCampaignToCache(data, dispatch)

				const action = api.util.updateQueryData(
					'campaignsPage',
					RECENT_PARAMS,
					draft => {
						const rest = draft.documents.slice(0, RECENT_PARAMS.$take! - 1)
						draft.documents = [data, ...rest]
						draft.total = draft.total + 1
					}
				)

				dispatch(action)
			},
			query: name => ({
				data: {
					name
				},
				method: 'POST',
				url: 'campaigns'
			})
		}),
		exportCampaign: create.mutation<string, Partial<OData.AudienceParams>>({
			query: params => ({
				method: 'GET',
				params,
				url: 'campaigns/export'
			})
		}),

		getCampaignsById: create.query<Campaign, string>({
			providesTags: [Tag.Campaign],
			query: id => ({
				url: `campaigns/${id}`
			})
		}),
		repliedExecutionsStatistics: create.query<
			RepliedCampaignExecutionsStatistics,
			string
		>({
			query: id => ({
				url: `campaigns/${id}/replied-executions-statistics`
			})
		}),
		runCampaign: create.mutation<Campaign, string>({
			invalidatesTags: [...PAGE_ID, Tag.ExecutionsStatistics],
			onQueryStarted: updateCacheCampaign,
			query: id => ({
				method: 'PUT',
				url: `campaigns/${id}/run`
			})
		}),
		stopCampaign: create.mutation<Campaign, string>({
			invalidatesTags: PAGE_ID,
			onQueryStarted: updateCacheCampaign,
			query: id => ({
				method: 'PUT',
				url: `campaigns/${id}/stop`
			})
		}),
		unArchiveCampaign: create.mutation<Campaign, string>({
			invalidatesTags: PAGE_ID,
			onQueryStarted: updateCacheCampaign,
			query: id => ({
				method: 'PUT',
				url: `campaigns/${id}/unarchive`
			})
		}),
		updateCampaignName: create.mutation<
			Campaign,
			Pick<Campaign, 'id' | 'name'>
		>({
			invalidatesTags: PAGE_ID,
			onQueryStarted: updateCacheCampaign,
			query: ({ id, name }) => ({
				data: {
					name
				},
				method: 'PUT',
				url: `campaigns/${id}/update`
			})
		})
	}),
	overrideExisting: true
})

export function addCampaignToCache(
	campaign: Campaign,
	dispatch: ThunkDispatch<any, any, any>
) {
	const action = api.util.upsertQueryData(
		'getCampaignsById',
		campaign.id,
		campaign
	)

	dispatch(action)
}

export function patchRecent(
	campaign: Campaign,
	dispatch: ThunkDispatch<any, any, any>
) {
	const action = api.util.updateQueryData(
		'campaignsPage',
		RECENT_PARAMS,
		draft => {
			const found = draft.documents.findIndex(({ id }) => id === campaign.id)
			if (found !== -1) {
				draft.documents[found] = campaign
			}
		}
	)

	return dispatch(action)
}

async function updateCacheCampaign(
	_: unknown,
	{
		dispatch,
		queryFulfilled
	}: {
		dispatch: ThunkDispatch<any, any, any>
		queryFulfilled: Promise<{ data: Campaign }>
	}
) {
	const { data } = await queryFulfilled
	addCampaignToCache(data, dispatch)
	patchRecent(data, dispatch)
}

export const refetchList = () => api.util.invalidateTags(PAGE_ID)
